import { useCallback, useState } from 'react';
import Modal from 'utils/packages/Modal';
import { formatAmount } from 'utils';
import { getUser } from 'actions/user';
import { getSharedPlans } from 'actions/sharedPlans';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { changeAchPaymentMethod as changeAchPaymentMethodAction, clearStripeFailedPayment, getAchEligibleAccounts, } from '../actions/stripe';
import showAchAccountConfirmModal from '../components/AchAccountConfirmModal';
import { showAchAccountPickerModal } from '../components/AchAccountSelectorModal';
import useStripePayment from './useStripePayment';
export const NO_ACH_ERROR = 'No eligible bank account is available.';
const useChangeAchPaymentMethod = () => {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const currentAchPaymentMethod = useAppSelector((s) => s.stripe.customer?.achPaymentMethod);
    const { handlePaymentIntentAction } = useStripePayment();
    const [isFetching, setIsFetching] = useState(false);
    const performPatch = useCallback(async (account) => {
        const resp = await dispatch(changeAchPaymentMethodAction({
            plaidItemId: account.plaidItemId,
            plaidAccountId: account.plaidAccountId,
        }));
        if (resp?.errorMessage) {
            throw new Error(resp.errorMessage);
        }
        const payload = resp?.payload;
        if (payload?.clientSecret && payload?.paymentIntentStatus) {
            try {
                await handlePaymentIntentAction(payload.clientSecret, payload.paymentIntentStatus);
            }
            catch (_e) {
                // Confirm action failed; backend has already swapped the PM. The
                // failure will resurface via emmaProStatus.paymentFailed.
            }
        }
        dispatch(clearStripeFailedPayment());
        dispatch(getUser());
        dispatch(getSharedPlans());
    }, [dispatch, handlePaymentIntentAction]);
    const start = useCallback(async () => {
        const resp = await dispatch(getAchEligibleAccounts());
        const eligibleAccounts = resp?.payload?.eligibleAccounts ?? [];
        if (!eligibleAccounts.length) {
            throw new Error(NO_ACH_ERROR);
        }
        const currentIdx = currentAchPaymentMethod
            ? eligibleAccounts.findIndex(({ plaidItemId, plaidAccountId }) => plaidItemId === currentAchPaymentMethod.plaidItemId &&
                plaidAccountId === currentAchPaymentMethod.plaidAccountId)
            : -1;
        const accounts = currentIdx > 0
            ? [
                eligibleAccounts[currentIdx],
                ...eligibleAccounts.slice(0, currentIdx),
                ...eligibleAccounts.slice(currentIdx + 1),
            ]
            : eligibleAccounts;
        const initialIndex = 0;
        const account = await new Promise((resolve) => {
            let resolved = false;
            const open = () => {
                showAchAccountPickerModal({
                    accounts,
                    initialIndex,
                    onContinue: async (selected) => {
                        resolved = true;
                        Modal.hide(() => resolve(selected));
                    },
                    onDismissed: () => {
                        if (!resolved) {
                            resolved = true;
                            resolve(null);
                        }
                    },
                });
            };
            if (Modal.isAnyModalOpen?.()) {
                Modal.hide(open);
            }
            else {
                open();
            }
        });
        if (!account)
            return;
        const failedAmount = store.getState().user.user.emmaProStatus.failedPaymentAmount;
        const ctaLabel = failedAmount
            ? `Pay ${formatAmount(failedAmount.amount, failedAmount.currency)}`
            : 'Save';
        await new Promise((resolve) => {
            const open = () => {
                showAchAccountConfirmModal({
                    account,
                    ctaLabel,
                    onConfirm: async () => {
                        setIsFetching(true);
                        try {
                            await performPatch(account);
                            Modal.hide(() => resolve());
                        }
                        catch (e) {
                            Modal.showError(e.message);
                            resolve();
                        }
                        finally {
                            setIsFetching(false);
                        }
                    },
                    onDismissed: () => resolve(),
                });
            };
            if (Modal.isAnyModalOpen?.()) {
                Modal.hide(open);
            }
            else {
                open();
            }
        });
    }, [currentAchPaymentMethod, performPatch]);
    return { start, performPatch, isFetching };
};
export default useChangeAchPaymentMethod;
